import * as Sentry from '@sentry/vue';
import { Replay } from '@sentry/vue';
import { BrowserTracing } from '@sentry/browser';
import type { Event, EventHint } from '@sentry/vue';

const FUNCTIONAL_COOKIE_GROUP_ID = 'C0003';

export default defineNuxtPlugin(async (nuxtApp) => {
  const { vueApp } = nuxtApp;
  const {
    public: { sentry: sentryConfig },
  } = useRuntimeConfig();

  await new Promise((resolve) => {
    const timeout = setTimeout(() => {
      clearInterval(interval);
      resolve(null);
    }, 5000);

    const interval = setInterval(() => {
      if (typeof window?.OptanonActiveGroups === 'string') {
        clearTimeout(timeout);
        clearInterval(interval);
        resolve(null);
      }
    }, 200);
  });

  const getConsentState = () => {
    const groups = window?.OptanonActiveGroups;
    return typeof groups === 'string' && groups.includes(FUNCTIONAL_COOKIE_GROUP_ID);
  };

  const previousConsent = getConsentState();

  const hasFunctionalConsent =
    typeof window.OptanonActiveGroups === 'string' && window.OptanonActiveGroups.includes(FUNCTIONAL_COOKIE_GROUP_ID);

  Sentry.init({
    app: [vueApp],
    dsn: sentryConfig.config.dsn,
    environment: sentryConfig.config.environment,
    release: sentryConfig.config.release,
    integrations: [
      new BrowserTracing({
        ...(useRouter()
          ? { routingInstrumentation: Sentry.vueRouterInstrumentation(useRouter(), { routeLabel: 'path' }) }
          : {}),
      }),
      ...(hasFunctionalConsent
        ? [
            new Replay({
              maskAllText: true,
              blockAllMedia: true,
              mutationBreadcrumbLimit: 1000,
              mutationLimit: 1500,
            }),
          ]
        : []),
    ],
    sampleRate: sentryConfig.config.sampleRate,
    tracesSampleRate: sentryConfig.config.tracesSampleRate,
    replaysOnErrorSampleRate: hasFunctionalConsent ? sentryConfig.config.replaysOnErrorSampleRate : 0,
    replaysSessionSampleRate: sentryConfig.config.replaysSessionSampleRate,
    maxBreadcrumbs: 25,
    beforeSend(event: Event, _hint: EventHint) {
      const value = event?.exception?.values?.[0];
      if (
        value &&
        value.type === 'EvalError' &&
        value?.stacktrace?.frames?.[0]?.filename &&
        value.stacktrace.frames[0].filename.toLowerCase().includes('_incapsula_resource')
      ) {
        return null;
      }
      // Continue sending to Sentry
      return event;
    },
  });

  vueApp.mixin(
    Sentry.createTracingMixins({ trackComponents: true, timeout: 2000, hooks: ['activate', 'mount', 'update'] }),
  );
  Sentry.attachErrorHandler(vueApp, {
    logErrors: false,
    attachProps: true,
    trackComponents: true,
    timeout: 2000,
    hooks: ['activate', 'mount', 'update'],
  });

  // Method responsible for restart sentry configuration plugin when functional cookies changed.
  window.addEventListener('OneTrustGroupsUpdated', () => {
    const currentConsent = getConsentState();
    if (currentConsent !== previousConsent && hasFunctionalConsent) {
      location.reload();
    }
  });

  return {
    provide: {
      sentry: {
        setContext: Sentry.setContext,
        setUser: Sentry.setUser,
        setTag: Sentry.setTag,
        addBreadcrumb: Sentry.addBreadcrumb,
        captureException: Sentry.captureException,
      },
    },
  };
});
